import {
  createAbsoluteUrl,
  createUrlResolver,
  siteNames,
} from '@seek/melways-sites';
import React from 'react';

import { getCountryFromSite } from 'src/siteConfig';
import { isEnvStaging } from 'src/utils/isEnvironment';

import type { ConfigOptions, ConfigUtils } from '../types';

interface ConfigProviderProps {
  children: React.ReactNode;
  config: ConfigOptions;
}

const defaultConfig: ConfigOptions = {
  branch: 'unknown',
  environment: 'dev',
  language: 'en',
  locale: 'en-AU',
  site: siteNames.employer.seek.au,
  version: 'unknown',
};

const defaultConfigUtils: ConfigUtils = {
  absoluteEmployerUrlResolver: () =>
    createAbsoluteUrl({
      product: 'employer',
      country: 'au',
      path: '/unknown',
      language: 'en-AU',
      staging: false,
    }),
  isAnz: true,
  isStaging: false,
  urlResolver: createUrlResolver({
    site: siteNames.employer.seek.au,
    language: 'en-AU',
    staging: false,
  }),
};

export const ConfigContext = React.createContext<ConfigOptions & ConfigUtils>({
  ...defaultConfig,
  ...defaultConfigUtils,
});

export const ConfigProvider: React.FC<ConfigProviderProps> = ({
  children,
  config,
}) => {
  const { environment, locale, site } = config;
  const isAnz =
    site === siteNames.employer.seek.au || site === siteNames.employer.seek.nz;
  const isStaging = isEnvStaging(environment);

  const urlResolver = createUrlResolver({
    site,
    language: locale,
    staging: isStaging,
  });

  const absoluteEmployerUrlResolver = (path: string) =>
    createAbsoluteUrl({
      product: 'employer',
      country: getCountryFromSite(site),
      path,
      staging: isStaging,
      language: locale,
    });

  const configUtils: ConfigUtils = {
    absoluteEmployerUrlResolver,
    isAnz,
    isStaging,
    urlResolver,
  };

  return (
    <ConfigContext.Provider value={{ ...config, ...configUtils }}>
      {children}
    </ConfigContext.Provider>
  );
};
